/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { useContext, useEffect, useState } from "react";
import {
  createCheckoutSession,
  getCoupons,
  getPlans,
  ProfileDetails,
  saveCardDetailsTostripe,
  verifyStudentDetails,
} from "../core/_requests";
import { ActivePlanDetails, Plan, PlanDetail } from "../core/_models";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import "../../../assets/custom-style.css";
import Header from "../../sidebar/Header";
import Footer from "../../sidebar/Footer";
import { loadStripe } from "@stripe/stripe-js";
import PlanUnsubscribe from "./PlanUnsubscribe";
import { useModalContext } from "../../Context/ModalContext";
import OfferBanner from "./OfferText";
import PaymentMethodModal from "./PaymentMethodModal";
import { useSubscription } from "../../Context/SubscriptionContext";
import dataimage from "../../../assets/ic_no_data.svg";

interface UserProfile {
  name: string;
  walletBalance: number;
  email?: string;
  profile?: string;
  contactNumber?: string;
}

interface Coupon {
  primaryId?: string;
  planId: string;
  code: string;
  description: string;
  couponType: number; // 1 = percentage, 2 = fixed
  amount: number;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
  applicablePlanIds?: string[];
}

type ButtonTexts = Record<string, string>;

const ListPlans = () => {
  const { id: user_id } = useParams();
  const { setSelectedPlan } = useSubscription();
  const navigate = useNavigate();

  // Existing states
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscribedActivePlan, setSubscribedActivePlan] = useState<
    Plan | null | any
  >(null);
  const [subscribingPlan, setSubscribingPlan] = useState<string | null>(null);
  const [selectedPlanOption, setSelectedPlanOption] = useState<{
    [key: string]: PlanDetail | null;
  }>({});
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [walletBalance, setWalletBalance] = useState<any>();
  const [showSubscribeConformationModal, setShowSubscribeConformationModal] =
    useState(false);
    const [subtype, setSubtype] = useState<any>(1);
  const [walletInputAmount, setWalletInputAmount] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeFeatureId, setActiveFeatureId] = useState<number | null>(null);
  const { showUnsubscribeModal, setShowUnsubscribeModal } = useModalContext();
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [message, setMessage] = useState("");
  const [activeMessageVisible, setActiveMessageVisible] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showTrialSubscribeModal, setShowTrialSubscribeModal] = useState(false);
  const [activePlanDetails, setActivePlanDetails] = useState<ActivePlanDetails>(
    {
      id: "",
      name: "",
      amount: 0,
      originalAmount: 0,
      type: 0,
      validity: 0,

      button: "",
      is_trial: false,
      plan_end_date: "",
      plan_start_date: "",
      yearDifferenceAmount: "",
      monthDifferenceAmount: "",
      description: "",
      plan_details: [],
    }
  );
  
  const [paymentType, setPaymentType] = useState<any>(1);
  const [buttonTexts, setButtonTexts] = useState<ButtonTexts>({});

  // Coupon related states
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
  const [couponError, setCouponError] = useState("");
  const [offerDescription, setOfferDescription] = useState<string>("");
  const [offerText, setOfferText] = useState<string>("");
  const [offerAmount, setOfferAmount] = useState<number | null>(null);
  const [offerToday, setOfferToday] = useState<boolean>(false);
  const [coupons, setCoupons] = useState<any>({});

  const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLIC_KEY || ""
  );

  // Existing helper functions
  const handleClick = () => setActiveMessageVisible(true);
  const handleResetMessage = () => setActiveMessageVisible(false);
  const handleShowUnsubscribeModal = () => setShowUnsubscribeModal(true);
  const handleTrialSubscribeModal = () =>
    setShowTrialSubscribeModal((prev) => !prev);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // Fetch profile details
const fetchProfileDetails = async () => {
  try {
    const res = await ProfileDetails(user_id);

    // Set user profile & wallet balance
    setUserProfile(res?.data);
    const balance = res.data?.walletBalance || 0;
    setWalletBalance(balance * 0.8);

    // Fetch user subscriptions if they exist
    const subscriptions = res?.data?.user_plan_subscriptions || [];

    if (subscriptions.length > 0) {
      // Take the first active subscription (or handle multiple)
      const activeSub = subscriptions.find((sub: any) => !sub.isUnsubscriped && sub.plan_status === 1);

      if (activeSub) {
        setSubtype(activeSub.plan?.plantypeId);
        setSubscribedActivePlan({
          id: activeSub.plan_id,
          name: activeSub.plan?.name,
          is_trial: activeSub.is_trial,
          type: activeSub.plan?.plantypeId,
          plan_details: [
            {
              amount: activeSub.amount,
              validity: activeSub.validity,
              type: activeSub.type,
              isActive: true,
              is_trial: activeSub.is_trial,
              validityCount: activeSub.validityCount,
              originalAmount: activeSub.originalAmount,
            },
          ],
        });
      }
    }
    
    
  } catch (err) {
    console.error("Error fetching profile details:", err);
    setWalletBalance(0);
  }
};


  // Fetch plans
  const fetchPlans = async () => {
    try {
      const res = await getPlans(user_id);
      const plansData: Plan[] = res?.data || [];
      setPlans(plansData);
      const activePlans = plansData.filter((plan: Plan) => plan.isPlanActive);
      const initialSelectedOptions: { [key: string]: PlanDetail | null } = {};

      activePlans.forEach((activePlan: Plan) => {
        setSubscribedActivePlan(activePlan);
        const activeDetail = activePlan.plan_details.find(
          (detail: PlanDetail) => detail.isActive
        );
        const selectedDetail = activeDetail || activePlan.plan_details[0];
      });

      // Initialize button texts
      setButtonTexts(
        plansData.reduce((acc, plan) => {
          acc[plan.id] = plan.button || "Subscribe";
          return acc;
        }, {} as ButtonTexts)
      );
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (subscribedActivePlan?.id
  
       
    ) {
      ;
    }
  }, [subscribedActivePlan]);
  

  useEffect(() => {
    if (subscribedActivePlan?.id) {
      localStorage.setItem("subscribedPlanId", subscribedActivePlan.id);

    }
  }, [activePlanDetails, selectedPlanOption]);
  // Fetch coupons
const fetchCoupons = async () => {
    try {
      const response = await getCoupons({ couponType: "subscription" });

      if (response.data && response.data.length > 0) {
        const coupon = response.data[0];
        setAppliedCoupon(coupon);

        // Check if coupon is active
        const now = new Date();
        const todayDate = new Date(
          now.toLocaleString("en-US", { timeZone: "America/New_York" })
        );
        const startDate = new Date(coupon.startDate || "");
        const endDate = new Date(coupon.endDate || "");

        if (todayDate >= startDate && todayDate <= endDate) {
          setOfferToday(true);
          setOfferDescription(
            `${coupon.description} - ${
              coupon.couponType === 1
                ? `${coupon.amount}%  OFF `
                : `$${coupon.amount} OFF`
            }`
          );
        } else {
          setOfferDescription("");
          setOfferToday(false);
        }
      }
    } catch (error) {
      console.error("Error fetching coupons:", error);
    }
  };
  


  // Apply coupon manually
  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError("Please enter a coupon code");
      return;
    }

    try {
     
      // For now, we'll use the existing coupon if the code matches
      if (appliedCoupon && appliedCoupon.code === couponCode) {
        setCouponError("");
        toast.success("Coupon applied successfully!");
      } else {
        setCouponError("Invalid coupon code");
        toast.error("Invalid coupon code");
      }
    } catch (error) {
      setCouponError("Failed to apply coupon");
      toast.error("Failed to apply coupon");
    }
  };

  // Calculate discounted amount
  const calculateDiscountedAmount = (
    originalAmount: number,
    coupon: Coupon | null
  ): number => {
    if (!coupon) return originalAmount;

    let discountedAmount = originalAmount;
    if (coupon.couponType === 1) {
      // Percentage discount
      discountedAmount =
        originalAmount - (originalAmount * coupon.amount) / 100;
    } else if (coupon.couponType === 2) {
      // Fixed amount
      discountedAmount = originalAmount - coupon.amount;
    }

    return Math.max(discountedAmount, 0); // Ensure amount doesn't go below 0
  };

  // Handle plan option change
  const handlePlanOptionChange = (
    planId: string,
    amount: number,
    validity: number,
    validityCount: string,
    type: any,
    isActive: boolean,
    is_trial: boolean,
    originalAmount: number
  ) => {
    if (isActive) {
      setActiveMessageVisible(true);
    } else {
      setActiveMessageVisible(false);
    }

    setIsChangingPlan(true);
    setSelectedPlanOption((prevState) => {
      const newState = { ...prevState };

      Object.keys(newState).forEach((key) => {
        if (key !== planId && newState[key]?.isActive === false) {
          newState[key] = null;
        }
      });

      newState[planId] = {
        amount,
        validityCount,
        validity,
        type,
        isActive,
        is_trial,
        originalAmount,
      };

      setButtonTexts((prevTexts) => {
        const updatedTexts = { ...prevTexts };
        updatedTexts[planId] =
          amount === 0
            ? "Activate Trial"
            : plans.find((p) => p.id === planId)?.button || "Subscribe";
        return updatedTexts;
      });

      return newState;
    });
    setIsChangingPlan(false);

    // Update offer details if applicable
    if (
      offerToday &&
      appliedCoupon &&
      (appliedCoupon.planId === "00000000-0000-0000-0000-000000000000" ||
        appliedCoupon.planId === planId)
    ) {
      const discount = calculateDiscountedAmount(amount, appliedCoupon);
      setOfferAmount(discount);
      setOfferText(
        appliedCoupon.couponType === 1
          ? `(${appliedCoupon.amount}% off, new price $${discount.toFixed(2)})`
          : `($${appliedCoupon.amount} off, new price $${discount.toFixed(2)})`
      );
    } else {
      setOfferAmount(null);
      setOfferText("");
    }
  };

  // Handle subscription
  const handleSubscribe = async (planId: string) => {
    if (!selectedPlanOption[planId]) {
      toast.error("Please select a plan option before subscribing");
      return;
    }

    const selectedOption = selectedPlanOption[planId]!;
    const { amount, validity, type } = selectedOption;
    const isTrial = buttonTexts[planId] === "Activate Trial";

    let effectivePaymentType = isTrial ? 4 : 2;
    let finalAmount =
      offerToday && appliedCoupon
        ? calculateDiscountedAmount(amount, appliedCoupon)
        : amount;
    let finalWalletInputAmount = 0;

    if (!isTrial) {
      if (paymentType === 1) {
        if (walletInputAmount <= walletBalance && walletBalance != 0) {
          if (walletInputAmount >= finalAmount) {
            effectivePaymentType = 1;
            finalWalletInputAmount = finalAmount;
          } else {
            effectivePaymentType = 3;
            finalWalletInputAmount = walletInputAmount;
            finalAmount = finalAmount - walletInputAmount;
          }
        } else {
          toast.error(
            "Entered wallet amount exceeds available wallet balance.",
            { autoClose: 5000 }
          );
          return;
        }
      } else if (paymentType === 2) {
        effectivePaymentType = 2;
        finalWalletInputAmount = 0;
      }
    }

    setSubscribingPlan(planId);
    handleCloseSubscribeModal();
    handleCloseSubscribeConformationModal();

    try {
      const sessionData = await createCheckoutSession({
        planId,
        originalAmount: amount,
        amount: finalAmount,
        user_id,
        type,
        validity,
        paymentType: effectivePaymentType,
        walletInputAmount: isTrial ? 0 : finalWalletInputAmount,
        is_trial: isTrial,
        ...(offerToday &&
          appliedCoupon && { coupon_id: appliedCoupon.primaryId }),
      });

      if (sessionData.status === true) {
        toast.success(sessionData.message, { autoClose: 5000 });
        scrollToTop();
        setSelectedPlanOption({});
        setButtonTexts((prevTexts) => {
          const updatedTexts = { ...prevTexts };
          updatedTexts[planId] = "Cancel";
          return updatedTexts;
        });
        fetchPlans();
        fetchProfileDetails();
      } else {
        toast.error(sessionData.message, { autoClose: 5000 });
      }

      if (!isTrial) {
        const stripe = await stripePromise;
        if (stripe) {
          localStorage.setItem("sessionId", sessionData.id);
          const { error } = await stripe.redirectToCheckout({
            sessionId: sessionData.id,
          });
          if (error) {
            toast.error("Error processing payment. Please try again.", {
              autoClose: 5000,
            });
          }
        }
      }
    } catch (error) {
      console.error("Error during subscription/payment process:", error);
    } finally {
      setSubscribingPlan(null);
    }
  };

  // Handle show subscribe modal
const handleShowSubscribeModal = (plan: any) => {
  const selectedDetail = selectedPlanOption[plan.id];

  // ✅ HARD SAFETY CHECK (TypeScript + Runtime)
  if (!selectedDetail) {
    toast.error("Pick a Plan First – Then Hit Subscribe!");
    return;
  }

  // ✅ TRIAL FLOW
  if (selectedDetail.amount === 0 || selectedDetail.is_trial) {
    setActivePlanDetails(plan as any);
    setShowTrialSubscribeModal(true);
    return;
  }

  // ✅ PAID FLOW
  navigate(`/subscription/checkout/${user_id}`, {
    state: {
      plan,
      selectedDetail, // ✅ now guaranteed NOT null
      walletBalance,
      offerToday,
      offerAmount,
      offerText,
      couponId: coupons?.primaryId || null,
      paymentType,
      walletInputAmount,
      userId: user_id,
    },
  });
};


  // Handle close subscribe modal
  const handleCloseSubscribeModal = () => {
    setShowSubscribeModal(false);
    if (walletBalance !== 0) {
      setShowSubscribeConformationModal(true);
    }
  };

  const handleCloseSubscribeConformationModal = () => {
    setWalletInputAmount(0);
    setShowSubscribeConformationModal(false);
  };

  // Handle trial subscription
  const handleTrialSubscription = (planId: string) => {
    const selectedPlan = selectedPlanOption[planId];
    if (!selectedPlan) {
      toast.error("Selected plan not found.");
      return;
    }
    navigate("/trial-plan", {
      state: { selectedPlan, activePlanDetails, planId, userProfile },
    });
  };
  const handleInfoClick = (id: number) => {
    setActiveFeatureId((prevId) => (prevId === id ? null : id));
  };
  // Effects
  useEffect(() => {
    if (subscribedActivePlan?.id) {
      localStorage.setItem("subscribedPlanId", subscribedActivePlan.id);
    }
  }, [subscribedActivePlan]);

  useEffect(() => {
    if (user_id) {
      localStorage.setItem("userId", user_id);
    }
  }, [user_id]);

  useEffect(() => {
    const selectedPlanAmount =
      offerToday && appliedCoupon && offerAmount
        ? offerAmount
        : selectedPlanOption[activePlanDetails?.id || ""]?.amount || 0;

    if (walletBalance >= selectedPlanAmount) {
      setWalletInputAmount(selectedPlanAmount);
      setPaymentType(1);
    } else {
      setWalletInputAmount(walletBalance);
      setPaymentType(3);
    }
  }, [
    activePlanDetails,
    walletBalance,
    selectedPlanOption,
    offerToday,
    appliedCoupon,
    offerAmount,
  ]);

  useEffect(() => {
    fetchPlans();
    fetchProfileDetails();
    fetchCoupons();
  }, [user_id]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (activeFeatureId !== null) {
      timer = setTimeout(() => {
        setActiveFeatureId(null);
      }, 3000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [activeFeatureId]);

  // Render
  return (
    <section className="container py-4">
      {/* Header */}
      <Header
        userProfile={userProfile}
        walletBalance={walletBalance}
        subscribedActivePlan={subscribedActivePlan}
      />
      <OfferBanner offerText={offerDescription} />

      {/* Plan List */}
      <div className="row justify-content-center">
        {loading ? (
          <div className="col-12 text-center">
            <p>Loading plans...</p>
          </div>
        ) : plans.length === 0 ? (
          <div className="col-12 text-center">
            <img
              src={dataimage}
              alt="Plan"
              style={{ width: "200px", height: "150px" }}
            />
            <p>No plans available at the moment.</p>
          </div>
        ) : (
          plans.map((plan) => {
            const { id, name, description } = plan;
            return (
              <div key={id} className="col-sm-12 col-md-6 col-lg-4 mb-4 ">
                <div
                  className={`plan-box card p-4 mb-4 ${
                    plan?.isPlanActive ? "bg-light-green" : ""
                  }`}
                  style={{
                    boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1)",
                    borderRadius: "10px",
                    transition: "transform 0.3s ease-in-out",
                  }}
                >
                  {/* Plan Header */}
                  <div className="text-center plan-header d-flex flex-column align-items-center">
                    {plan.icon && (
                      <img
                        className="img-fluid mb-2"
                        src={plan.icon}
                        alt="FRAYO"
                        height="90px"
                        width="90px"
                      />
                    )}

                    <h5 className="plan-type fs-12 text-success mb-0 fw-bold text-center mb-2">
                      {name}
                      {plan.isPlanActive && (
                        <span className="badge bg-danger ms-3">
                          {plan.is_trial ? "Trial" : ""} Active
                        </span>
                      )}
                    </h5>

                    {description && (
                      <p
                        className="fs-12 text-center mt-2"
                        style={{ maxWidth: "500px" }}
                      >
                        {description}
                      </p>
                    )}
                  </div>

                  {/* Pricing Options */}
               <div>
  <strong>Pricing Options:</strong>
  {plan?.plan_details?.map((detail: PlanDetail) => (
    <div key={detail.validity} className="form-check mt-3 mb-3 position-relative">

      {/* Show Active Badge */}
      {plan.isPlanActive && detail.isActive && (
        <span
          className="badge bg-success position-absolute"
          style={{ top: "0", right: "0", fontSize: "0.8rem", padding: "0.4rem 0.6rem" }}
        >
          Active
        </span>
      )}

      {/* If this is the active plan detail, show plain text instead of radio button */}
      {plan.isPlanActive && detail.isActive ? (
        <label
          className="form-check-label fw-bold text-success"
          style={{
            fontSize: "1rem",
            marginLeft: "0.5rem",
          }}
        >
          ${detail.amount.toFixed(2)} per {detail.validityCount}
        </label>
      ) : (
        <>
          <input
            type="radio"
            id={`plan-${plan.id}-${detail.validity}`}
            name={`plan-${plan.id}`}
            value={detail.amount}
            className="form-check-input"
            onChange={() => {
              handlePlanOptionChange(
                plan.id,
                detail.amount,
                detail.validity,
                detail.validityCount,
                detail.type,
                detail.isActive,
                detail.is_trial,
                detail.originalAmount
              );
            }}
            checked={
              selectedPlanOption[plan.id]?.type === detail.type &&
              selectedPlanOption[plan.id]?.amount === detail.amount 
            }
            disabled={subscribedActivePlan?.type === detail.type && subscribedActivePlan?.id === plan.id}
          />
          <label
            className={`form-check-label ${
              detail.amount === 0
                ? "text-danger fw-bold"
                : detail.isActive ||
                  (plan.isPlanActive && detail.isActive)
                ? "text-muted"
                : ""
            }`}
            htmlFor={`plan-${plan.id}-${detail.validity}`}
            style={{
              fontSize: "0.9rem",
              marginLeft: "0.5rem",
            }}
          >
            ${detail.amount.toFixed(2)} per {detail.validityCount}
          </label>
        </>
      )}
    </div>
  ))}
</div>


                  {/* Features */}
                  <div className="plan-details mt-3">
                    <strong className="text-danger">Features:</strong>
                    <ul className="list-unstyled">
                      {plan.featuresDetails.map((feature: any) => (
                        <li
                          key={feature.id}
                          className="d-flex align-items-center my-2"
                        >
                          <img
                            src="/assets/images/vector.png"
                            alt="tick"
                            className="me-2"
                            style={{ width: "15px", height: "15px" }}
                          />
                          <span className="me-3" style={{ fontSize: "14px" }}>
                            {feature.name}&nbsp;
                            {feature.description && (
                              <i
                                className="material-symbols-outlined info-ico me-3"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                title={feature.description}
                                style={{
                                  cursor: "pointer",
                                  position: "relative",
                                  top: "4px",
                                  fontSize: "16px",
                                  width: "20px",
                                  height: "20px",
                                }}
                                onClick={() => handleInfoClick(feature.id)}
                              >
                                info
                              </i>
                            )}
                            {activeFeatureId === feature.id && (
                              <div
                                style={{
                                  background: "#fff",
                                  padding: "10px",
                                  borderRadius: "5px",
                                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                                  border: "1px solid #ccc",
                                  position: "absolute",
                                  zIndex: 1,
                                }}
                              >
                                {feature.description}
                              </div>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {plan?.plantype?.planTypeName !== "Free Plan" &&
                    plan.button && (
                      <>
                        {activeMessageVisible && (
                          <p className="text-danger fw-bold mt-2 mb-2">
                            This plan is already active. Please choose a
                            different plan to upgrade.
                          </p>
                        )}

                        {plan?.isTrialUsed === false && (
                          <p className="text-danger fw-bold mt-2 mb-2">
                            Start your 30-day free trial now and explore all
                            features!
                          </p>
                        )}

                        <div className="col-lg-12 plan-footer d-flex justify-content-center align-items-center mt-0">
                          {plan?.isTrialUsed === false && (
                            <button
                              className="btn rounded-5 btn-outline-danger me-2"
                              onClick={() => {
                                if (selectedPlanOption[plan.id]) {
                                  setActivePlanDetails(plan as any);
                                  if (
                                    !plan.isVerifiedStudent &&
                                    plan.name
                                      .toLocaleLowerCase()
                                      .includes("student")
                                  ) {
                                    const selectedPlan =
                                      selectedPlanOption[plan.id];
                                    if (selectedPlan) {
                                      const data: any = {
                                        id: selectedPlan.id ?? "",
                                        amount: selectedPlan.amount ?? 0,
                                        validity: selectedPlan.validity ?? 0,
                                        type: selectedPlan.type ?? 1,
                                        is_trial: true,
                                      };
                                      setSelectedPlan(data);
                                    }
                                    localStorage.setItem("PlanId", plan.id);
                                    navigate(
                                      `/student-verification/${user_id}`
                                    );
                                  } else {
                                    handleTrialSubscribeModal();
                                  }
                                } else {
                                  toast.error(
                                    "Please select a plan option before subscribing"
                                  );
                                }
                              }}
                            >
                              Activate Trial
                            </button>
                          )}

                          {selectedPlanOption[plan.id]?.isActive !== true ||
                          plan.button === "Cancel" ? (
                            <button
                              className={`btn rounded-5 me-2 ${
                                subscribingPlan === plan?.id
                                  ? "btn-outline-primary"
                                  : buttonTexts[plan.id] === "Activate Trial"
                                  ? "btn-outline-danger"
                                  : buttonTexts[plan.id] === "Upgrade"
                                  ? "btn-outline-success"
                                  : "btn-outlin-secondary"
                              }`}
                              onClick={() => {
                                handleResetMessage();
                                if (!selectedPlanOption[plan.id]) {
                                  scrollToTop();
                                  toast.error(
                                    "Please select a plan option before subscribing"
                                  );
                                  return;
                                }
                                if (
                                  plan.isHigherPlan &&
                                  selectedPlanOption[plan.id]?.type === 2
                                ) {
                                  scrollToTop();
                                  toast.error("You're Already in Annual Plan");
                                  return;
                                }
                                if (plan.button === "Cancel") {
                                  handleShowUnsubscribeModal();
                                }
                                if (
                                  !plan.isVerifiedStudent &&
                                  plan.name
                                    .toLocaleLowerCase()
                                    .includes("student")
                                ) {
                                  const selectedPlan =
                                    selectedPlanOption[plan.id];
                                  if (selectedPlan) {
                                    const data: any = {
                                      id: selectedPlan.id ?? "",
                                      amount: selectedPlan.amount ?? 0,
                                      validity: selectedPlan.validity ?? 0,
                                      type: selectedPlan.type ?? 1,
                                      is_trial: false,
                                    };
                                    setSelectedPlan(data);
                                  }
                                  localStorage.setItem("PlanId", plan.id);
                                  navigate(`/student-verification/${user_id}`);
                                } else {
                                  handleShowSubscribeModal(plan);
                                }
                              }}
                            >
                              {subscribingPlan === plan?.id
                                ? "Processing..."
                                : buttonTexts[plan.id] || plan.button}
                            </button>
                          ) : (
                            <button
                              className="btn rounded-5 btn-outline-secondary me-2"
                              onClick={handleClick}
                            >
                              Active
                            </button>
                          )}
                        </div>
                      </>
                    )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <Footer />

      {/* Payment Method Modal */}
      <PaymentMethodModal
        show={showSubscribeConformationModal}
        onClose={() => setShowSubscribeConformationModal(false)}
        offer={offerToday && !!appliedCoupon}
        offerAmount={offerAmount || 0}
        selectedPlanOption={selectedPlanOption}
        activePlanDetails={activePlanDetails}
        paymentType={paymentType}
        setPaymentType={setPaymentType}
        walletBalance={walletBalance}
        walletInputAmount={walletInputAmount}
        setWalletInputAmount={setWalletInputAmount}
        handleSubscribe={handleSubscribe}
      />

      {/* Trial Plan Subscribe Modal */}
      <Modal
        show={showTrialSubscribeModal}
        onHide={handleTrialSubscribeModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Activate Trial Plan to {activePlanDetails?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="card mb-3">
            <div className="card-header text-center text-warning">
              <span role="img" aria-label="alert" style={{ fontSize: "24px" }}>
                ⚠️
              </span>{" "}
              Trial Plan Information
            </div>
            <div className="card-body text-center">
              <h5 className="card-title text-success fw-bold">
                Trial Plan Activation
              </h5>
              <p>
                This plan is a <strong>trial</strong>. You can only activate a
                trial plan once. Please proceed if you want to use your trial
                for this plan:
              </p>
              <h4 className="text-center text-danger">
                {activePlanDetails?.name}
              </h4>
              <p>
                Validity :{" "}
                {selectedPlanOption[activePlanDetails?.id || ""]
                  ?.validityCount === "Month"
                  ? "Monthly"
                  : selectedPlanOption[activePlanDetails?.id || ""]
                      ?.validityCount === "Year"
                  ? "Annual"
                  : ""}
              </p>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-center">
          <button
            className="btn btn-cancel rounded-5 px-5 mt-2 mb-2"
            onClick={handleTrialSubscribeModal}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary rounded-5 px-5 mt-2 mb-2"
            onClick={() => {
              handleTrialSubscription(activePlanDetails.id);
            }}
          >
            Confirm Trial Activation
          </button>
        </Modal.Footer>
      </Modal>

      {/* Subscribe Modal */}
      <Modal
        show={showSubscribeModal}
        onHide={handleCloseSubscribeModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {activePlanDetails?.button === "upgrade"
              ? "Upgrade"
              : activePlanDetails?.button === "downgrade"
              ? "Downgrade"
              : "Subscribe"}{" "}
            to {activePlanDetails?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Coupon Input Section */}
          <div className="mb-3">
           
            {couponError && (
              <div className="text-danger small mt-1">{couponError}</div>
            )}
          
          </div>

          {subscribedActivePlan && activePlanDetails?.button === "Upgrade" ? (
            <>
              <div className="card mb-3">
                <div className="card-header text-center text-warning">
                  <span
                    role="img"
                    aria-label="alert"
                    style={{ fontSize: "24px" }}
                  >
                    ⚠️
                  </span>{" "}
                  Active Plan Information
                </div>
                <div className="card-body text-center">
                  <h5 className="card-title text-success fw-bold">
                    You already have an active plan:
                  </h5>
                  <p className="card-text">
                    <strong className="text-danger">
                      {subscribedActivePlan?.name} {""}{" "}
                      {subscribedActivePlan?.is_trial === true &&
                        " (Trial plan)"}
                    </strong>
                  </p>
                  <p>
                    {subscribedActivePlan.plan_details.map((plan: any) =>
                      plan.isActive ? (
                        <span key={plan.id} className="text-success">
                          ${plan?.amount ? plan.amount.toFixed(2) : "0.00"} for{" "}
                          {plan?.validityCount}
                        </span>
                      ) : null
                    )}
                  </p>
                </div>
              </div>

              <p>Are you sure you want to upgrade to the plan?</p>
              <h4 className="text-center text-danger">
                {activePlanDetails?.name}
              </h4>
              <p>
                Validity:{" "}
                {offerToday && appliedCoupon && offerAmount ? (
                  <>
                    <span
                      style={{ textDecoration: "line-through", color: "gray" }}
                    >
                      $
                      {selectedPlanOption[
                        activePlanDetails?.id || ""
                      ]?.amount?.toFixed(2)}
                    </span>{" "}
                    <span className="text-success fw-bold">
                      ${offerAmount?.toFixed(2)}
                    </span>{" "}
                  </>
                ) : (
                  <span className="me-1">
                    $
                    {selectedPlanOption[
                      activePlanDetails?.id || ""
                    ]?.amount?.toFixed(2)}
                  </span>
                )}
                per{" "}
                {selectedPlanOption[activePlanDetails?.id || ""]?.validityCount}
              </p>
            </>
          ) : subscribedActivePlan &&
            activePlanDetails?.button === "Downgrade" ? (
            <>
              <div className="card mb-3">
                <div className="card-header text-center text-warning">
                  <span
                    role="img"
                    aria-label="alert"
                    style={{ fontSize: "24px" }}
                  >
                    ⚠️
                  </span>{" "}
                  Active Plan Information
                </div>
                <div className="card-body text-center">
                  <h5 className="card-title text-success fw-bold">
                    You already have an active plan:
                  </h5>
                  <p className="card-text">
                    <strong className="text-danger">
                      {subscribedActivePlan?.name}
                      {""}{" "}
                      {subscribedActivePlan?.is_trial === true &&
                        " (Trial plan)"}
                    </strong>
                  </p>
                  <p>
                    {subscribedActivePlan.plan_details.map((plan: any) =>
                      plan.isActive ? (
                        <span key={plan.id} className="text-success">
                          ${plan?.amount ? plan.amount.toFixed(2) : "0.00"} for{" "}
                          {plan?.validityCount}
                        </span>
                      ) : null
                    )}
                  </p>
                </div>
              </div>

              <p>Are you sure you want to downgrade to the plan:</p>
              <h4 className="text-center text-danger">
                {activePlanDetails?.name}
              </h4>
              <p>
                Validity: $
                {offerToday && appliedCoupon && offerAmount
                  ? offerAmount.toFixed(2)
                  : selectedPlanOption[
                      activePlanDetails?.id || ""
                    ]?.amount.toFixed(2)}{" "}
                for{" "}
                {selectedPlanOption[activePlanDetails?.id || ""]?.validityCount}
              </p>
            </>
          ) : (
            <>
              <p>Are you sure you want to subscribe to the plan:</p>
              <h4 className="text-center text-danger">
                {activePlanDetails?.name}
              </h4>
              <p>
                Validity: $
                {offerToday && appliedCoupon && offerAmount
                  ? offerAmount.toFixed(2)
                  : selectedPlanOption[
                      activePlanDetails?.id || ""
                    ]?.amount.toFixed(2)}{" "}
                for{" "}
                {selectedPlanOption[activePlanDetails?.id || ""]?.validityCount}
              </p>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-center">
          <button
            className="btn btn-cancel rounded-5 px-5 mt-2 mb-2"
            onClick={handleCloseSubscribeModal}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary rounded-5 px-5 mt-2 mb-2"
            onClick={() => {
              if (
                walletBalance > 1 &&
                selectedPlanOption[activePlanDetails?.id || ""]?.is_trial ===
                  false
              ) {
                handleCloseSubscribeModal();
              } else if (walletBalance > 1) {
                handleCloseSubscribeModal();
              } else {
                handleSubscribe(activePlanDetails.id);
              }
            }}
          >
            Confirm{" "}
            {activePlanDetails?.button === "Upgrade"
              ? "Upgrade"
              : activePlanDetails?.button === "Downgrade"
              ? "Downgrade"
              : "Subscription"}
          </button>
        </Modal.Footer>
      </Modal>

      {showUnsubscribeModal && (
        <PlanUnsubscribe showFinalModal={showUnsubscribeModal} />
      )}
    </section>
  );
};

export default ListPlans;
